import * as React from 'react';
import { ActionButton, ComboBox, Dropdown } from 'office-ui-fabric-react';
import { buildPredecessorsMap, IPredecessorInfo, ITask, PredecessorType, PredecessorTypeMetadata } from '../../../entities/Subentities';
import NumberInput from '../../common/inputs/NumberInput';
import LabellableComponent from '../../common/LabellableComponent';
import { isAllowedAsPredecessorByHierarchy, TaskScheduleCalculator } from '../Fields';
import { buildPredecessorSnapshot, getAssigneeCalendars } from '../../utils/duration';
import { Dictionary } from '../../../entities/common';
import { Resource } from '../../../store/ResourcesListStore';
import TaskStartDueDatePickerInput from '../../common/inputs/TaskStartDueDatePickerInput';
import { toDate, toDictionaryById } from '../../utils/common';
import { Validator } from '../../../validation';
import { FormatType } from '../../../entities/Metadata';
import ComboboxPicker from '../../common/inputs/ComboboxPicker';
import { IFindResult } from '../../common/inputs/EntityPickerInput';
import { ResourceOperations } from '../../../store/permissions';
import { fetchProjectTasksForPredecessors } from '../taskFetchers';
import { ProjectInfo } from '../../../store/ProjectsListStore';
import { CalendarDataSet } from '../../../store/calendar/common';

interface Props {
    task: ITask;
    tasks: ITask[];
    project?: ProjectInfo;
    isExternal?: boolean;
    calendar: CalendarDataSet;
    resources: Dictionary<Resource>;
    calendarsById: Dictionary<CalendarDataSet>;
    useResourceCalendar: boolean;
    shiftZeroDayMilestoneWithFSPredecessorToNextWorkingDay?: boolean;
    predecessor?: IPredecessorInfo;
    externalTask?: ITask;
    onApply: (predecessor: IPredecessorInfo) => void;
    onCancel: () => void;
}

const pickerMinMaxLag = { min: -1000, max: 1000 };
// US 16555: lag is a decimal number of working days.
const lagValidator = Validator.new().required().decimal().min(pickerMinMaxLag.min).max(pickerMinMaxLag.max).build();

const TaskPredecessorEditBox = (props: Props) => {
    const isExternalMode = props.isExternal ?? false;
    const [externalTasks, setExternalTasks] = React.useState<ITask[]>([]);
    const [selectedProjectId, setSelectedProjectId] = React.useState<string | undefined>(props.predecessor?.projectId);
    const [selectedProjectName, setSelectedProjectName] = React.useState<string | undefined>(props.predecessor?.projectName);

    const [predecessorType, setPredecessorType] = React.useState(props.predecessor?.type ?? PredecessorType.FinishToStart);
    const [predecessorId, setPredecessorId] = React.useState(props.predecessor?.id);
    const [lag, setLag] = React.useState<number>(props.predecessor?.lag ?? 0);
    const [estimatedDate, setEstimatedDate] = React.useState<Date>();

    React.useEffect(() => {
        if (!isExternalMode || !selectedProjectId) {
            setExternalTasks([]);
            return;
        }

        fetchProjectTasksForPredecessors(selectedProjectId)
            .then(tasks => setExternalTasks(tasks));
    }, [isExternalMode, selectedProjectId]);

    const availableTasks = React.useMemo(() => {
        if (!isExternalMode) {
            return props.tasks;
        }

        if (props.externalTask && predecessorId && props.externalTask.id === predecessorId
            && !externalTasks.some(task => task.id === predecessorId)) {
            return [props.externalTask, ...externalTasks];
        }

        return externalTasks;
    }, [isExternalMode, externalTasks, props.tasks, props.externalTask, predecessorId]);

    const predecessorsMap = React.useMemo(() => buildPredecessorsMap(availableTasks), [availableTasks]);
    const predecessorOptions = React.useMemo(() => availableTasks.filter(_ => _.id !== props.task?.id
        && (isExternalMode || isAllowedAsPredecessorByHierarchy(props.task, _))
        && !predecessorsMap[_.id]?.[props.task.id]
        && !props.task.attributes.Predecessor?.some(pr => pr.id === _.id && props.predecessor?.id !== pr.id))
        .map(_ => ({ key: _.id, text: _.attributes.Name })), [availableTasks, props.task, predecessorsMap, isExternalMode]);
    const existingPredecessorOption = React.useMemo(() => {
        if (!props.predecessor || props.predecessor.projectId !== selectedProjectId) {
            return undefined;
        }

        return {
            key: props.predecessor.id,
            text: props.predecessor.name
        };
    }, [props.predecessor, selectedProjectId]);
    const hasNoTasksForProject = React.useMemo(() => isExternalMode && !!selectedProjectId && externalTasks.length === 0,
        [isExternalMode, selectedProjectId, externalTasks.length]);
    const predecessorOptionsWithEmpty = React.useMemo(() => {
        if (hasNoTasksForProject) {
            if (existingPredecessorOption) {
                return [existingPredecessorOption];
            }
            return [{ key: 'noTasksFound', text: 'No tasks found', disabled: true }];
        }

        return predecessorOptions;
    }, [hasNoTasksForProject, predecessorOptions, existingPredecessorOption]);

    const typeOptions = React.useMemo(() => [PredecessorType.FinishToStart, PredecessorType.StartToStart, PredecessorType.FinishToFinish, PredecessorType.StartToFinish]
        .map(_ => ({ key: _, text: `${PredecessorTypeMetadata[_].label} (${PredecessorTypeMetadata[_].abbreviation})` })), []);
    const tasksMap = React.useMemo(() => toDictionaryById(availableTasks ?? []), [availableTasks]);

    const isPredecessorToStart = React.useMemo(() => predecessorType === PredecessorType.FinishToStart || predecessorType === PredecessorType.StartToStart,
        [predecessorType]);
    const isPredecessorFromStart = React.useMemo(() => predecessorType === PredecessorType.StartToStart || predecessorType === PredecessorType.StartToFinish,
        [predecessorType]);

    // The successor's own calendars, used only where the dictated date is PREVIEWED (below) — both for the
    // Estimated Date itself and for which days its picker offers. The engine snaps a dictated Start onto a day
    // the assignees actually work, so the preview must too or it promises a date the save will move.
    const assigneeCalendars = React.useMemo(
        () => getAssigneeCalendars(props.useResourceCalendar, props.task.attributes.AssignedTo, props.calendar, props.resources, props.calendarsById),
        [props.useResourceCalendar, props.task.attributes.AssignedTo, props.calendar, props.resources, props.calendarsById]);

    // The lag-0 anchor the Estimated Date picker measures against. Deliberately the PROJECT calendar, not the
    // assignee calendars: onDateChange derives the lag from this anchor with GetLag, and the lag walk itself is
    // a project-calendar quantity (TaskWorkloadHandler.Dictate), so the derived lag only round-trips back to
    // the picked date when both ends are measured on the same calendar the walk uses.
    const dictatedDate = React.useMemo(() => {
        const predecessorTask = predecessorId !== undefined ? tasksMap[predecessorId] : undefined;
        const dates = predecessorTask
            ? TaskScheduleCalculator.GetDatesDictatedByPredecessor(
                predecessorTask,
                { id: predecessorId!, name: predecessorTask.attributes.Name, type: predecessorType, lag: 0 },
                props.calendar,
                [props.calendar],
                props.shiftZeroDayMilestoneWithFSPredecessorToNextWorkingDay ?? true,
                props.task)
            : {};
        return dates.startDate ?? dates.dueDate;
    }, [
        props.calendar,
        props.shiftZeroDayMilestoneWithFSPredecessorToNextWorkingDay,
        props.task,
        tasksMap,
        predecessorId,
        predecessorType]);

    const onPredecessorChange = React.useCallback((_, option) => {
        setPredecessorId(option?.key as string);
        setLag(0);
    }, []);
    const onProjectPickerComplete = React.useCallback((project?: IFindResult) => {
        setSelectedProjectId(project?.id);
        setSelectedProjectName(project?.name);
        setPredecessorId(undefined);
        setLag(0);
    }, []);
    const onTypeChange = React.useCallback((_, option) => {
        setPredecessorType(option?.key as PredecessorType);
        setLag(0);
    }, []);
    const onLagChange = React.useCallback((value) => setLag(value), []);
    const onDateChange = React.useCallback((value) => {
        const picked = toDate(value);
        if (dictatedDate && picked) {
            // US 16555: the picker yields a date-only value, but a fractional predecessor's lag-0 anchor can be
            // mid-day. Snap the picked day to the working-day start (To-Start deps) or end (To-Finish deps) —
            // matching dictateDate's anchors — so the derived decimal lag reproduces that day when applied.
            const target = isPredecessorToStart ? picked.setDayStart(props.calendar) : picked.setDayEnd(props.calendar);
            setLag(TaskScheduleCalculator.GetLag(target, dictatedDate, props.calendar));
        }
    }, [props.calendar, dictatedDate, isPredecessorToStart])

    React.useEffect(() => {
        const predecessorTask = predecessorId ? tasksMap[predecessorId] : undefined;
        if (predecessorId && predecessorTask && typeof lag === 'number') {
            const dates = TaskScheduleCalculator.GetDatesDictatedByPredecessor(
                predecessorTask,
                { id: predecessorId, name: predecessorTask.attributes.Name, type: predecessorType, lag },
                props.calendar,
                assigneeCalendars,
                props.shiftZeroDayMilestoneWithFSPredecessorToNextWorkingDay ?? true,
                props.task);
            setEstimatedDate(dates.startDate ?? dates.dueDate);
        } else {
            setEstimatedDate(undefined);
        }
    }, [
        props.task,
        props.calendar,
        assigneeCalendars,
        props.shiftZeroDayMilestoneWithFSPredecessorToNextWorkingDay,
        tasksMap,
        predecessorId,
        predecessorType,
        lag])

    const isApplyAvailable = React.useMemo(() => predecessorId && lagValidator.isValid(lag)
        && (!isExternalMode || !!selectedProjectId), [predecessorId, lag, isExternalMode, selectedProjectId]);
    const onApply = React.useCallback(() => {
        if (predecessorId && typeof lag === 'number' && (!isExternalMode || selectedProjectId)) {
            const predecessorTask = tasksMap[predecessorId];
            const taskName = predecessorTask?.attributes?.Name;
            if (!taskName) {
                return;
            }

            if (isExternalMode) {
                props.onApply({
                    id: predecessorId,
                    name: taskName,
                    type: predecessorType,
                    lag,
                    projectId: selectedProjectId,
                    projectName: selectedProjectName,
                    ...buildPredecessorSnapshot(predecessorType, predecessorTask.attributes)
                });
                return;
            }

            props.onApply({ id: predecessorId, name: taskName, type: predecessorType, lag });
        }
    }, [props.onApply, props.predecessor, tasksMap, predecessorType, predecessorId, lag, isExternalMode, selectedProjectId, selectedProjectName]);

    const predecessorDoesNotDictateDate = React.useMemo(() => !predecessorId || !dictatedDate, [predecessorId, dictatedDate]);
    const pickerMinMaxDates = React.useMemo(() => ({
        minDate: dictatedDate?.clone().addWorkingDays(pickerMinMaxLag.min, props.calendar),
        maxDate: dictatedDate?.clone().addWorkingDays(pickerMinMaxLag.max, props.calendar)
    }), [props.calendar, dictatedDate]);

    const estimatedDateTitle = React.useMemo(() => `Task ${isPredecessorToStart ? "Start" : "Due"} Date will be defined based on \
Predecessor ${isPredecessorFromStart ? "Start" : "Due"} Date, selected dependency type and lead/lag time. \
Estimated ${isPredecessorToStart ? "Start" : "Due"} Date is for information only. Task’s ${isPredecessorToStart ? "Start" : "Due"} Date can be changed from Details tab.`,
        [isPredecessorFromStart, isPredecessorToStart]);

    return <div className="edit-predecessor-box">
        <div className="title">{props.predecessor ? "Editing" : "Adding"} {isExternalMode ? "external " : ""}predecessor</div>
        {isExternalMode &&
            <div className="grid-item">
                <ComboboxPicker
                    inputProps={{ placeholder: "Select project", autoFocus: true }}
                    searchUrl="api/project/find"
                    filter={{ accessLevel: ResourceOperations.Read, exceptIds: props.project?.id ? [props.project.id] : undefined }}
                    selectedKey={selectedProjectId}
                    selectedText={selectedProjectName}
                    onEditComplete={onProjectPickerComplete}
                />
            </div>}
        <div className="grid-item">
            <ComboBox
                calloutProps={{ className: hasNoTasksForProject ? "empty-predecessor-picker-callout" : undefined }}
                placeholder={isExternalMode ? "Select task..." : undefined}
                disabled={isExternalMode && !selectedProjectId}
                selectedKey={predecessorId}
                options={predecessorOptionsWithEmpty}
                onChange={onPredecessorChange}
                autoComplete='on'
                allowFreeform
                useComboBoxAsMenuWidth
            />
        </div>
        <div className="grid-item">
            <Dropdown
                className="predecessor-type"
                selectedKey={predecessorType}
                options={typeOptions}
                onChange={onTypeChange}
            />
        </div>
        <div className="grid-item estimated-date-box">
            <LabellableComponent label='Lag'
                description="To add lead time, type a negative number of working days. To add lag time, type a positive number of working days."
                className="field-container">
                <NumberInput value={lag} format={FormatType.Days} disabled={predecessorDoesNotDictateDate} validator={lagValidator} onChanged={onLagChange} />
            </LabellableComponent>
            <LabellableComponent label="Estimated Date"
                description={estimatedDateTitle}
                className="field-container">
                {/* Only days the successor's assignees actually work are selectable: the dictated date is placed
                    on one of those days anyway, so offering the others would promise a date the save then moves. */}
                <TaskStartDueDatePickerInput minDate={pickerMinMaxDates.minDate} maxDate={pickerMinMaxDates.maxDate}
                    value={estimatedDate?.toUniversalIsoDateTimeString()}
                    assigneeCalendars={assigneeCalendars} useResourceCalendar={props.useResourceCalendar}
                    showTime disabled={predecessorDoesNotDictateDate} onChanged={onDateChange} />
            </LabellableComponent>
        </div>
        <div>
            <ActionButton className="apply-btn" disabled={!isApplyAvailable} primary onClick={onApply}>Apply</ActionButton>
            <ActionButton onClick={props.onCancel}>Cancel</ActionButton>
        </div>
    </div>;
};
export default TaskPredecessorEditBox;